import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link, useParams, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";

import BlockUserModal from "../../../Components/Common/BlockUserModal";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Loader from "../../../Components/Common/Loader";
import ProfileInfoSection from "../../../Components/Common/ProfileInfoSection";
import UserProfileAvatarWithFallback from "../../../Components/Common/UserProfileAvatarWithFallback";
import {
  CERTIFICATION_LEVEL_OPTIONS,
  getCertificationMeta,
} from "../../../constants/certification";
import { useCertifyProfessional } from "../../../hooks/useProfessionals";
import { useUpdateUserActivities, useUser } from "../../../hooks/useUser";
import { useBlockUser, useUnblockUser } from "../../../hooks/useUsersList";
import { normalizeBackendFileUrl } from "../../../utils/backendApi";

const USER_TYPE_LABELS = {
  particular: "Particulier",
  professional: "Professionnel du BTP",
  enterprise: "Entreprise",
  bank: "Banque",
  organism: "Organisme",
  admin: "Administrateur",
};

const ROLE_LABELS = {
  SUPER_ADMIN: "Super administrateur",
  ADMIN: "Administrateur",
  ASSISTANT: "Assistant",
  COMMITTEE: "Membre du comité",
  USER: "Utilisateur",
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

/**
 * User detail page.
 *
 * Rebuilt in full: it used to call `/profile/:id/` — the mobile self-profile
 * route, which returns a different shape and refuses to read someone else's
 * profile — and only rendered identity, address and company. It now uses the
 * admin route, displays labels instead of raw ids, and carries the actions an
 * admin needs on a user.
 */
function UserDetail(props) {
  const { id } = useParams();
  const userQ = useUser(id);
  const blockUserMutation = useBlockUser();
  const unblockUserMutation = useUnblockUser();
  const certifyProfessional = useCertifyProfessional({ method: "patch" });

  const [blockModal, setBlockModal] = useState({ open: false, mode: "block" });
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [certificateValue, setCertificateValue] = useState(0);
  const updateActivities = useUpdateUserActivities();
  const [activitiesOpen, setActivitiesOpen] = useState(false);
  const [activitiesValue, setActivitiesValue] = useState("");

  useEffect(() => {
    document.title = props.t("Fiche utilisateur | Le Conseiller du BTP");
  }, [props.t]);

  const user = userQ.data || {};
  const profile = user.profile || {};
  const professional = user.professional || null;
  // Enterprises and organisms declare "Activités". The API exposes it as
  // `activities` for both; the raw fallbacks read the `activite` column each
  // type keeps it in. An enterprise's `domain` is its sector, a different
  // field shown on its own row below — it used to be read as the activities.
  const isStructure =
    profile.user_type === "enterprise" || profile.user_type === "organism";
  const isEnterprise = profile.user_type === "enterprise";
  const structureActivities =
    user.activities ?? user.enterprise?.activite ?? user.organism?.activite ?? null;
  const isBlocked = user.is_active === false;
  const certificationLevel = professional?.banca_certificate ?? 0;

  const confirmBlockAction = useCallback(
    (reason) => {
      const onSuccess = () => setBlockModal((p) => ({ ...p, open: false }));
      if (blockModal.mode === "block") {
        blockUserMutation.mutate({ id, reason }, { onSuccess });
      } else {
        unblockUserMutation.mutate(id, { onSuccess });
      }
    },
    [blockModal.mode, blockUserMutation, unblockUserMutation, id]
  );

  const submitCertification = useCallback(async () => {
    const professionalId = professional?.id;
    if (!professionalId) return;
    try {
      await certifyProfessional.mutateAsync({
        id: professionalId,
        payload: { banca_certificate: Number(certificateValue) },
      });
      setCertifyOpen(false);
      userQ.refetch();
    } catch (_err) {
      // handled by hook toast
    }
  }, [certificateValue, certifyProfessional, professional, userQ]);

  if (userQ.isLoading) return <Loader />;
  if (userQ.isError) {
    return (
      <Loader
        error={
          userQ.error?.message || props.t("Impossible de charger cette fiche")
        }
      />
    );
  }

  const fullName =
    [profile.first_name, profile.last_name].filter(Boolean).join(" ") ||
    user.username ||
    "-";

  const identityItems = [
    { label: props.t("Nom"), value: profile.last_name },
    { label: props.t("Prénom(s)"), value: profile.first_name },
    { label: props.t("Nom d'utilisateur"), value: user.username },
    { label: props.t("Email"), value: user.email },
    { label: props.t("Téléphone"), value: user.phone_number },
  ];

  const accountItems = [
    {
      label: props.t("Type d'utilisateur"),
      value: USER_TYPE_LABELS[profile.user_type] || profile.user_type,
    },
    {
      label: props.t("Rôle"),
      value: ROLE_LABELS[user.role_code] || user.role_code,
    },
    { label: props.t("Catégorie d'institutions"), value: profile.category },
    { label: props.t("Institution"), value: profile.subcategory },
    ...(isEnterprise
      ? [{ label: props.t("Domaine"), value: user.enterprise?.domain }]
      : []),
    ...(isStructure
      ? [{ label: props.t("Activités"), value: structureActivities }]
      : []),
    { label: props.t("Dernière connexion"), value: formatDate(user.last_login) },
  ];

  const addressItems = [
    { label: props.t("Pays"), value: profile.country },
    { label: props.t("Province"), value: profile.province },
    { label: props.t("Ville"), value: profile.city },
    { label: props.t("Arrondissement"), value: profile.arrondissement },
    { label: props.t("Quartier"), value: profile.quartier },
    { label: props.t("Adresse"), value: profile.address },
  ];

  const certificationMeta = getCertificationMeta(certificationLevel);
  const cvUrl = normalizeBackendFileUrl(professional?.cv);

  const professionalItems = professional
    ? [
        {
          label: props.t("Niveau de certification"),
          value: `${certificationMeta.label}${
            certificationLevel > 0 ? ` (${certificationLevel}/5)` : ""
          }`,
        },
        {
          label: props.t("Certifié"),
          value: professional.is_certified ? props.t("Oui") : props.t("Non"),
        },
        {
          label: props.t("Expert"),
          value: professional.is_expert ? props.t("Oui") : props.t("Non"),
        },
        {
          label: props.t("Dossier validé"),
          value: professional.is_validated ? props.t("Oui") : props.t("Non"),
        },
      ]
    : [];

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      <BlockUserModal
        show={blockModal.open}
        mode={blockModal.mode}
        user={user}
        onConfirm={confirmBlockAction}
        onCloseClick={() => setBlockModal((p) => ({ ...p, open: false }))}
        isPending={blockUserMutation.isPending || unblockUserMutation.isPending}
      />

      <Container fluid>
        <BreadCrumb
          title={props.t("Fiche utilisateur")}
          pageTitle={props.t("Utilisateurs")}
        />

        {isBlocked ? (
          <div className="alert alert-danger" role="alert">
            <i className="ri-lock-line align-bottom me-1"></i>
            <strong>{props.t("Compte bloqué")}</strong>
            {user.blocked_reason ? ` — ${user.blocked_reason}` : ""}
            {user.blocked_at ? ` (${formatDate(user.blocked_at)}` : ""}
            {user.blocked_by
              ? `, par ${user.blocked_by})`
              : user.blocked_at
                ? ")"
                : ""}
          </div>
        ) : null}

        <Row>
          <Col xl={4}>
            <Card>
              <CardBody className="text-center">
                <UserProfileAvatarWithFallback
                  picture={profile.profile_pic}
                  name={fullName}
                  t={props.t}
                />
                <h5 className="mt-3 mb-1">{fullName}</h5>
                <p className="text-muted mb-2">{user.email}</p>

                <div className="d-flex justify-content-center gap-2 flex-wrap">
                  <span
                    className={`badge ${
                      isBlocked ? "badge-soft-danger" : "badge-soft-success"
                    }`}
                  >
                    {isBlocked ? props.t("Bloqué") : props.t("Actif")}
                  </span>
                  {professional?.is_certified ? (
                    <span className={certificationMeta.badgeClass}>
                      {certificationMeta.label}
                    </span>
                  ) : null}
                </div>

                <div className="d-flex flex-column gap-2 mt-4">
                  {isBlocked ? (
                    <Button
                      color="success"
                      onClick={() =>
                        setBlockModal({ open: true, mode: "unblock" })
                      }
                    >
                      <i className="ri-lock-unlock-line align-bottom me-1"></i>
                      {props.t("Réactiver l'accès")}
                    </Button>
                  ) : (
                    <Button
                      color="danger"
                      onClick={() =>
                        setBlockModal({ open: true, mode: "block" })
                      }
                    >
                      <i className="ri-lock-line align-bottom me-1"></i>
                      {props.t("Bloquer l'accès")}
                    </Button>
                  )}

                  {professional ? (
                    <Button
                      color="warning"
                      onClick={() => {
                        setCertificateValue(certificationLevel || 1);
                        setCertifyOpen((v) => !v);
                      }}
                    >
                      <i className="ri-award-line align-bottom me-1"></i>
                      {professional.is_certified
                        ? props.t("Modifier la certification")
                        : props.t("Certifier")}
                    </Button>
                  ) : null}

                  {certifyOpen && professional ? (
                    <div className="border rounded p-3 text-start">
                      <label className="form-label" htmlFor="certif-level">
                        {props.t("Niveau de certification")}
                      </label>
                      <select
                        id="certif-level"
                        className="form-select mb-2"
                        value={certificateValue}
                        onChange={(e) => setCertificateValue(e.target.value)}
                      >
                        {CERTIFICATION_LEVEL_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-muted fs-12">
                        {getCertificationMeta(certificateValue).description}
                      </p>
                      <Button
                        color="warning"
                        size="sm"
                        className="w-100"
                        onClick={submitCertification}
                        disabled={certifyProfessional.isPending}
                      >
                        {props.t("Enregistrer")}
                      </Button>
                    </div>
                  ) : null}

                  {isStructure ? (
                    <Button
                      color="warning"
                      onClick={() => {
                        setActivitiesValue(structureActivities || "");
                        setActivitiesOpen((v) => !v);
                      }}
                    >
                      <i className="ri-briefcase-line align-bottom me-1"></i>
                      {structureActivities
                        ? props.t("Modifier les activités")
                        : props.t("Renseigner les activités")}
                    </Button>
                  ) : null}
                  {activitiesOpen && isStructure ? (
                    <div className="border rounded p-3 text-start">
                      <label className="form-label" htmlFor="structure-activities">
                        {props.t("Activités")}
                      </label>
                      <textarea
                        id="structure-activities"
                        className="form-control mb-2"
                        rows={4}
                        value={activitiesValue}
                        onChange={(e) => setActivitiesValue(e.target.value)}
                        placeholder={props.t("Décrivez les activités de la structure")}
                      />
                      <Button
                        color="warning"
                        size="sm"
                        className="w-100"
                        disabled={updateActivities.isPending}
                        onClick={() =>
                          updateActivities.mutate(
                            { id, activities: activitiesValue.trim() || null },
                            { onSuccess: () => setActivitiesOpen(false) }
                          )
                        }
                      >
                        {props.t("Enregistrer")}
                      </Button>
                    </div>
                  ) : null}
                  <Link to="/pages-team" className="btn btn-light">
                    {props.t("Retour à la liste")}
                  </Link>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={8}>
            <ProfileInfoSection
              title={props.t("Identité")}
              items={identityItems}
            />
            <ProfileInfoSection title={props.t("Compte")} items={accountItems} />
            <ProfileInfoSection
              title={props.t("Localisation")}
              items={addressItems}
            />
            {professional ? (
              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">
                    {props.t("Profil professionnel")}
                  </h5>
                  <ProfileInfoSection
                    title=""
                    items={professionalItems}
                    className="border-0 shadow-none mb-0"
                  />
                  {cvUrl ? (
                    <a
                      href={cvUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-soft-primary btn-sm mt-2"
                    >
                      <i className="ri-file-text-line align-bottom me-1"></i>
                      {props.t("Voir le CV")}
                    </a>
                  ) : (
                    <p className="text-muted fs-12 mb-0">
                      {props.t("Aucun CV déposé")}
                    </p>
                  )}
                </CardBody>
              </Card>
            ) : null}
          </Col>
        </Row>
      </Container>
    </div>
  );
}

UserDetail.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(UserDetail));
